import { useEffect, useState, useMemo } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Download } from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
} from "recharts";
import {
  donationAPI,
  fundingStatusAPI,
  type DonationDetail,
  type FundingStatusData,
} from "../../services/api";
import logoImage from "../../../Jainboarding_User/assets/JBF logo.png";

const CHART_COLORS = [
  "hsl(var(--primary))",
  "hsl(var(--accent))",
  "hsl(var(--success))",
  "hsl(var(--muted))",
];

function formatDate(iso: string) {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function getPaymentStatus(donation: DonationDetail): string {
  const payments = donation.payments;
  if (!payments?.length) {
    return "—";
  }
  const latest = payments[0];
  return latest.status ?? "—";
}

export default function Funding() {
  const [fundingStatus, setFundingStatus] = useState<FundingStatusData | null>(
    null
  );
  const [donationList, setDonationList] = useState<DonationDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [printingDonation, setPrintingDonation] =
    useState<DonationDetail | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      setLoading(true);
      setError(null);
      try {
        const [status, donations] = await Promise.all([
          fundingStatusAPI.get(),
          donationAPI.getDonationDetails(),
        ]);
        if (!cancelled) {
          setFundingStatus(status);
          setDonationList(donations);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "Failed to load funding data"
          );
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleDownloadReceipt = (donation: DonationDetail) => {
    setPrintingDonation(donation);
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const pieChartData = useMemo(() => {
    const byType = fundingStatus?.by_type ?? [];
    if (!byType.length) {
      return [];
    }
    return byType.map((item, i) => ({
      name: item.donation_type,
      value: item.percentage,
      amount: item.amount,
      color: CHART_COLORS[i % CHART_COLORS.length],
    }));
  }, [fundingStatus?.by_type]);

  const monthlyTrendData = useMemo(() => {
    const map = new Map<number, { month: string; amount: number }>();
    donationList.forEach(d => {
      try {
        const date = new Date(d.created_at);
        const sortKey = date.getFullYear() * 12 + date.getMonth();
        const monthLabel = date.toLocaleString("en-IN", {
          month: "short",
          year: "numeric",
        });
        const existing = map.get(sortKey);
        if (existing) {
          existing.amount += parseFloat(d.amount || "0");
        } else {
          map.set(sortKey, {
            month: monthLabel,
            amount: parseFloat(d.amount || "0"),
          });
        }
      } catch {
        // skip
      }
    });
    return Array.from(map.entries())
      .sort((a, b) => a[0] - b[0])
      .slice(-6)
      .map(([, v]) => ({ month: v.month, amount: v.amount }));
  }, [donationList]);

  if (loading) {
    return (
      <div className="flex min-h-[200px] items-center justify-center p-6">
        <p className="text-muted-foreground">Loading donation status...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6 p-6">
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          {error}
        </div>
      </div>
    );
  }

  const totalFunding = fundingStatus?.total_funding ?? 0;
  const byType = fundingStatus?.by_type ?? [];

  return (
    <div className="p-6">
      {/* Printable Receipt Layout - Hidden by default, visible only during print */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 0 !important;
          }
          html, body {
            height: 297mm !important;
            width: 210mm !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          body * {
            visibility: hidden;
          }
          #printable-receipt, #printable-receipt * {
            visibility: visible;
          }
          #printable-receipt {
            position: absolute;
            left: 0;
            top: 0;
            width: 212mm;
            height: 295mm;
            max-height: 297mm;
            background-color: white !important;
            margin: 0 !important;
            padding: 0 !important;
            border: 2px solid #555 !important;
            box-sizing: border-box !important;
            display: flex !important;
            flex-direction: column !important;
            overflow: hidden !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}</style>

      {printingDonation && (
        <div
          id="printable-receipt"
          className="hidden flex-col bg-white font-sans print:flex"
        >
          {/* Header */}
          <div className="flex items-center justify-between bg-[#7A1E1E] px-10 py-8 text-white">
            <div className="flex items-center gap-3 bg-white px-3 py-2">
              <img src={logoImage} alt="JBF Logo" className="h-14 w-auto" />
            </div>

            <div className="text-right text-xs leading-tight">
              <p className="text-lg font-bold">Jain Boarding Federation</p>
              <p>Serving the Community with Dedication</p>

              <p className="mt-1">Email: info@jbf.org.in </p>
              <p> Phone: +91 8169074905</p>
              <p>
                Address: B-208,(South Wing) Malad Shopping Centre,
                <br />
                S. V. Road, Malad(West) Mumbai - 400064, India
              </p>
            </div>
          </div>

          {/* Title */}
          <div className="border-b bg-[#E9DCCB] py-2 text-center">
            <h3 className="text-lg font-bold tracking-wide text-[#8B2C2C]">
              DONATION RECEIPT
            </h3>
          </div>

          <div className="flex-grow px-10 py-8">
            {/* Receipt Info */}
            <div className="mb-6 flex justify-between text-sm">
              <div>
                <p className="text-gray-500">Receipt No.</p>
                <p className="font-semibold">
                  {printingDonation.payments?.[0]?.receipt_no || "-"}
                  {/* JBF/{new Date(printingDonation.created_at).getFullYear()}/
                  {1000 + Number(printingDonation.id)} */}
                </p>
              </div>

              <div className="text-right">
                <p className="text-gray-500">Date</p>
                <p className="font-semibold">
                  {formatDate(printingDonation.created_at)}
                </p>
              </div>
            </div>

            {/* Donor Information */}
            <div className="mb-6">
              <h4 className="mb-3 border-b pb-1 text-sm font-bold">
                Donor Information
              </h4>

              <div className="grid grid-cols-2 gap-y-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Name</p>
                  <p className="font-semibold">{printingDonation.name}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Address</p>
                  <p className="font-semibold">
                    {printingDonation.organization ?? "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Phone</p>
                  <p className="font-semibold">{printingDonation.phone}</p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Email</p>
                  <p className="font-semibold">{printingDonation.email}</p>
                </div>
              </div>
            </div>

            {/* Donation Details */}
            <div className="mb-6">
              <h4 className="mb-3 border-b pb-1 text-sm font-bold">
                Donation Details
              </h4>

              <div className="mb-4">
                <p className="text-xs text-gray-500">Amount (₹)</p>
                <p className="text-2xl font-bold">
                  ₹{" "}
                  {parseFloat(printingDonation.amount ?? 0).toLocaleString(
                    "en-IN"
                  )}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-y-4 text-sm">
                <div>
                  <p className="text-xs text-gray-500">Payment Mode</p>
                  <p className="font-semibold uppercase">
                    {printingDonation.payments?.[0]?.payment_method}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">
                    Transaction ID / Cheque No.
                  </p>
                  <p className="font-semibold">
                    {printingDonation.payments?.[0]?.razorpay_payment_id ??
                      "N/A"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">Purpose of Donation</p>
                  <p className="font-semibold">
                    {printingDonation.donation_type || "-"}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-gray-500">UPI ID</p>
                  <p className="font-semibold">
                    {printingDonation.payments?.[0]?.payment_method_details
                      ?.vpa ?? "-"}
                  </p>
                </div>
              </div>
            </div>

            {/* Tax Note */}
            <div className="mb-10 rounded-md border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
              <span className="font-semibold">Note:</span> This donation is
              eligible for tax exemption under Section 80G of the Income Tax
              Act, 1961. Please retain this receipt for your tax filing
              purposes.
            </div>

            {/* Signature */}
            <div className="mt-10 flex items-end justify-between">
              <p className="text-sm text-gray-600">Received with thanks</p>

              <div className="w-52 text-center">
                <div className="mb-1 border-t border-gray-600" />
                <p className="text-sm font-semibold">Authorized Signatory</p>
                <p className="text-xs text-gray-500">
                  Jain Boarding Federation
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-auto flex-shrink-0 border-t bg-gray-100 py-4 text-center text-[11px] text-gray-600">
            <p>
              This is a computer-generated receipt and does not require a
              physical signature.
            </p>
            <p className="font-medium">
              Thank you for your generous contribution!
            </p>
          </div>
        </div>
      )}

      <div className="mt-0 print:hidden">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Donation Status
          </h1>
          <p className="text-muted-foreground">
            Monitor funding distribution and donation list
          </p>
        </div>

        {/* Summary Cards: Total + by donation type */}
        <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                Total Funding
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-foreground">
                ₹{totalFunding.toLocaleString("en-IN")}
              </div>
            </CardContent>
          </Card>
          {byType.map(item => (
            <Card key={item.donation_type}>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {item.donation_type}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-foreground">
                  ₹{Number(item.amount).toLocaleString("en-IN")}
                </div>
                <p className="text-sm text-muted-foreground">
                  {item.percentage}% of total
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Charts */}
        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle>Funding Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              {pieChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={pieChartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={(props: { name: string; value: number }) =>
                        `${props.name} ${props.value}%`
                      }
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieChartData.map(entry => (
                        <Cell key={`cell-${entry.name}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(
                        value: number,
                        _name: string,
                        props: { payload?: { amount?: number } }
                      ) => [
                        `${value}%`,
                        props?.payload?.amount !== null
                          ? `₹${Number(props?.payload?.amount ?? 0).toLocaleString("en-IN")}`
                          : "",
                      ]}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                  No distribution data
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Monthly Funding Trend</CardTitle>
            </CardHeader>
            <CardContent>
              {monthlyTrendData.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={monthlyTrendData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip
                      formatter={(value: number) => [
                        `₹${Number(value).toLocaleString("en-IN")}`,
                      ]}
                    />
                    <Line
                      type="monotone"
                      dataKey="amount"
                      stroke="hsl(var(--primary))"
                      strokeWidth={2}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-[300px] items-center justify-center text-muted-foreground">
                  No trend data
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Donation List Table */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Donation List</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Sr. No</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Donation Type</TableHead>
                  <TableHead>Payment Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead className="text-center">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {donationList.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      className="text-center text-muted-foreground"
                    >
                      No donations yet
                    </TableCell>
                  </TableRow>
                ) : (
                  donationList.map((row, index) => {
                    const status = getPaymentStatus(row);
                    return (
                      <TableRow
                        key={row.id}
                        className="cursor-pointer hover:bg-muted"
                      >
                        <TableCell>{index + 1}</TableCell>
                        <TableCell className="font-medium">
                          {row.name}
                        </TableCell>
                        <TableCell>{row.email}</TableCell>
                        <TableCell>{row.phone}</TableCell>
                        <TableCell>
                          ₹
                          {parseFloat(row.amount || "0").toLocaleString(
                            "en-IN"
                          )}
                        </TableCell>
                        <TableCell>{row.donation_type}</TableCell>
                        <TableCell>
                          <Badge
                            variant={
                              status === "success"
                                ? "default"
                                : status === "created" || status === "pending"
                                  ? "secondary"
                                  : "outline"
                            }
                            className={
                              status === "success"
                                ? "bg-green-600 text-white"
                                : ""
                            }
                          >
                            {status}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(row.created_at)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            {/* <Button
                              size="sm"
                              variant="outline"
                              className="h-8 w-8 p-0"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </Button> */}
                            <Button
                              size="sm"
                              variant="outline"
                              className={`h-8 gap-1.5 bg-blue-100 hover:bg-accent/80 ${status !== "success" ? "cursor-not-allowed opacity-50" : ""}`}
                              onClick={e => {
                                e.stopPropagation();
                                if (status === "success") {
                                  handleDownloadReceipt(row);
                                }
                              }}
                              disabled={status !== "success"}
                              title="Download PDF Receipt"
                            >
                              <Download className="h-4 w-4 text-blue-600" />
                              <span className="text-xs">Receipt</span>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
